/**
 * UsageContext — single fetch + cache for the current user's plan/limits.
 *
 * The chip in the navbar, the limit-hit upgrade modal, and the studio
 * progress meter all consume this so we don't fan out N copies of
 * GET /api/user/usage.
 *
 * Auto-refreshes after content generation events via `refreshUsage()`.
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { devWarn } from "@/lib/uiStorage";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UsageContext = createContext(null);

export function UsageProvider({ children }) {
  const { user } = useAuth();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  const refreshUsage = useCallback(async () => {
    if (!user) { setUsage(null); return; }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/user/usage`);
      setUsage(r.data);
    } catch (err) {
      devWarn("UsageContext fetch failed", err?.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refreshUsage(); }, [refreshUsage]);

  // Cross-component refresh: any page (e.g. ContentStudio) can fire
  // `window.dispatchEvent(new Event('lumora:usage-changed'))` after a
  // successful generation so the navbar chip stays in sync.
  useEffect(() => {
    const h = () => refreshUsage();
    window.addEventListener("lumora:usage-changed", h);
    return () => window.removeEventListener("lumora:usage-changed", h);
  }, [refreshUsage]);

  return (
    <UsageContext.Provider value={{ usage, loading, refreshUsage }}>
      {children}
    </UsageContext.Provider>
  );
}

export function useUsage() {
  const ctx = useContext(UsageContext);
  return ctx || { usage: null, loading: false, refreshUsage: () => {} };
}

/** Helper: percent used (0-100). Returns 0 for unlimited (-1). */
export function usagePercent(used, limit) {
  if (limit === -1) return 0;
  if (!limit) return 100;
  return Math.min(Math.round((used / limit) * 100), 100);
}
